/**
 * CATMAN-S38 — Distingue "la request nunca llegó al servidor" de "el servidor contestó un error".
 *
 * Son dos situaciones opuestas para el usuario y hasta ahora colapsaban en el mismo cartel rojo:
 *
 * - **Corte de red:** no hay nada que hacer y no hay nada roto. Vuelve la conexión y el autoguardado
 *   sigue solo, así que el editor lo avisa sin suspender la cadena de reintentos.
 * - **Error del backend:** el guardado no va a funcionar por más que se reintente; ahí sí conviene
 *   interrumpir y suspender hasta que un guardado manual confirme que el canal volvió.
 *
 * `status: 0` es la firma del primer caso: el browser no obtuvo respuesta HTTP (DNS, red caída,
 * servidor inalcanzable, CORS). El `TimeoutError` del pipe cuenta igual: hubo un minuto sin
 * respuesta, que desde la pantalla es indistinguible de estar sin conexión. Nada más entra acá:
 * la promesa de "se reintenta solo" sólo se hace cuando hay motivos para creerla.
 *
 * `navigator.onLine === false` es prueba positiva de que el equipo está desconectado, pero su
 * inverso NO prueba nada (da `true` con wifi conectado y sin internet), así que sólo se usa para
 * decidir si vale la pena intentar el POST, nunca para clasificar un error ya ocurrido.
 */

export function isNetworkFailure(error?: any): boolean {
  if (error == null) return false;

  const status = Number(error?.status ?? error?.statusCode);
  if (status === 0) return true;

  //Un minuto sin respuesta: desde la pantalla es indistinguible de estar sin conexión.
  //Un error que no es ninguna de las dos cosas NO se asume corte de red: decirle "sin conexión" a
  //algo que no sabemos qué es, y encima prometerle un reintento que puede no servir nunca, es peor
  //que tratarlo como el fallo que efectivamente fue. Cae en el camino de error y corta la cadena.
  return error?.name === 'TimeoutError';
}

/** false sólo cuando el browser afirma estar desconectado. Ante la duda, se intenta igual. */
export function isBrowserOnline(): boolean {
  const online = typeof navigator !== 'undefined' ? navigator.onLine : undefined;
  return online !== false;
}
